/**
 * Roadmap Item Editor Component
 *
 * Modal for creating or editing roadmap items.
 */

import {
  useCreateRoadmapItem,
  useUpdateRoadmapItem,
  useDeleteRoadmapItem,
  useSetRoadmapItemBaseline,
  useInvestments,
  workflowService,
  investmentKeys,
  workflowKeys,
} from '@medsuite/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermissions } from '../../../hooks/usePermissions';
import { countBusinessDays } from '../../../utils/businessDays';

import { VarianceSummary } from './VarianceSummary';

import type { RoadmapItemWithInvestment, RoadmapThemeWithItems } from '@medsuite/models';

interface RoadmapItemEditorProps {
  item: RoadmapItemWithInvestment | null;
  roadmapId: number;
  themes: RoadmapThemeWithItems[];
  onClose: () => void;
}

/**
 * RoadmapItemEditor Component
 *
 * Features:
 * - Create new roadmap items
 * - Edit existing items
 * - Delete items with confirmation
 * - Investment selection
 * - Date range selection
 */
export function RoadmapItemEditor({ item, roadmapId: _roadmapId, themes, onClose }: RoadmapItemEditorProps) {
  const isEditing = item !== null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManageStatus } = usePermissions();

  // Form state
  const [themeId, setThemeId] = useState<number>(
    item?.roadmap_theme_id || themes[0]?.roadmap_theme_id || 0
  );
  const [investmentId, setInvestmentId] = useState<number>(item?.investment_id || 0);
  const [startDate, setStartDate] = useState<string>(
    item?.start_date ? new Date(item.start_date).toISOString().split('T')[0] : ''
  );
  const [endDate, setEndDate] = useState<string>(
    item?.end_date ? new Date(item.end_date).toISOString().split('T')[0] : ''
  );
  const [isCommitted, setIsCommitted] = useState<boolean>(item?.is_committed || false);
  const [committedDate, setCommittedDate] = useState<string>(
    item?.committed_date
      ? new Date(item.committed_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  );
  const [deliveryDate, setDeliveryDate] = useState<string>(
    item?.delivery_date ? new Date(item.delivery_date).toISOString().split('T')[0] : ''
  );
  const [toleranceDays, setToleranceDays] = useState<number>(item?.tolerance_days || 0);
  const [tolerancePercent, setTolerancePercent] = useState<number>(item?.tolerance_percent || 0);
  const [estimatedEffortDays, setEstimatedEffortDays] = useState<number>(
    item?.estimated_effort_days || 0
  );
  const [estimatedRemainingDays, setEstimatedRemainingDays] = useState<number>(
    item?.estimated_remaining_days || 0
  );
  const [topPriorityFlag, setTopPriorityFlag] = useState<boolean>(item?.top_priority_flag || false);

  // Auto-effort: track whether the user has manually overridden the effort value.
  // Treat 0 (the default) as "never set" so existing items get auto-calculated.
  const [effortManualOverride, setEffortManualOverride] = useState<boolean>(() => {
    if (!isEditing || !item) {
      return false;
    }
    // If effort is 0 (default), it was never intentionally set -- allow auto-calc
    if (item.estimated_effort_days === 0) {
      return false;
    }
    // Otherwise, only consider it overridden if it differs from the auto-calc
    const autoCalc = countBusinessDays(
      item.start_date ? new Date(item.start_date).toISOString().split('T')[0] : '',
      item.end_date ? new Date(item.end_date).toISOString().split('T')[0] : ''
    );
    return item.estimated_effort_days !== autoCalc;
  });

  const autoEffort = countBusinessDays(startDate, endDate);
  const isAutoEffort = !effortManualOverride && estimatedEffortDays === autoEffort;

  // Auto-calculate effort when dates change and user hasn't manually overridden
  const updateEffortFromDates = useCallback(
    (start: string, end: string) => {
      if (!effortManualOverride) {
        setEstimatedEffortDays(countBusinessDays(start, end));
      }
    },
    [effortManualOverride]
  );

  // On mount: auto-calculate effort for existing items that haven't been manually set
  useEffect(() => {
    if (!effortManualOverride && startDate && endDate) {
      const calc = countBusinessDays(startDate, endDate);
      if (calc > 0 && estimatedEffortDays !== calc) {
        setEstimatedEffortDays(calc);
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local baseline state so the UI updates immediately after mutation
  const [baselineDate, setBaselineDate] = useState<string | null>(
    item?.baseline_date ? String(item.baseline_date) : null
  );
  const [baselineStartDate, setBaselineStartDate] = useState<string | null>(
    item?.baseline_start_date ? String(item.baseline_start_date) : null
  );
  const [baselineEndDate, setBaselineEndDate] = useState<string | null>(
    item?.baseline_end_date ? String(item.baseline_end_date) : null
  );

  // Mark as planned (only for new items, checked by default)
  const [markAsPlanned, setMarkAsPlanned] = useState<boolean>(true);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Investment search
  const [investmentSearch, setInvestmentSearch] = useState('');
  const { data: investments = [] } = useInvestments();

  // Collect investment IDs already on this roadmap (from all themes)
  const existingInvestmentIds = new Set(
    themes.flatMap((theme) => theme.items?.map((i) => i.investment_id) || [])
  );

  // Filter investments: only show approved or submitted status, match search term,
  // and exclude investments already on this roadmap (unless editing that specific item)
  const filteredInvestments = investments.filter((inv) => {
    // Only include investments with approved or submitted status
    const hasValidStatus = inv.status === 'approved' || inv.status === 'submitted';
    // Match search term
    const matchesSearch = inv.title?.toLowerCase().includes(investmentSearch.toLowerCase());
    // Exclude investments already on the roadmap, unless it's the one being edited
    const isCurrentItem = isEditing && item?.investment_id === inv.investment_id;
    const notAlreadyOnRoadmap = !existingInvestmentIds.has(inv.investment_id) || isCurrentItem;
    return hasValidStatus && matchesSearch && notAlreadyOnRoadmap;
  });

  // Mutations
  const createMutation = useCreateRoadmapItem();
  const updateMutation = useUpdateRoadmapItem();
  const deleteMutation = useDeleteRoadmapItem();
  const baselineMutation = useSetRoadmapItemBaseline();

  // Update theme when themes change
  useEffect(() => {
    if (!item && themes.length > 0 && !themeId) {
      setThemeId(themes[0].roadmap_theme_id);
    }
  }, [themes, item, themeId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!themeId || !investmentId || !startDate || !endDate) {
      return;
    }

    // If markAsPlanned is checked, also mark the roadmap item as committed
    const shouldBeCommitted = isCommitted || (markAsPlanned && canManageStatus && !isEditing);

    const data = {
      roadmap_theme_id: themeId,
      investment_id: investmentId,
      start_date: startDate,
      end_date: endDate,
      is_committed: shouldBeCommitted,
      committed_date: committedDate,
      delivery_date: deliveryDate || null,
      tolerance_days: toleranceDays,
      tolerance_percent: tolerancePercent,
      estimated_effort_days: estimatedEffortDays,
      estimated_remaining_days: estimatedRemainingDays,
      top_priority_flag: topPriorityFlag,
    };

    try {
      if (isEditing && item) {
        await updateMutation.mutateAsync({ id: item.roadmap_item_id, data });
      } else {
        await createMutation.mutateAsync(data);

        // If markAsPlanned is checked and user has permission, update investment status
        if (markAsPlanned && canManageStatus) {
          try {
            const response = await workflowService.createDecision(investmentId, {
              investment_status: 'P', // Planned
              decision_rationale: 'Added to roadmap',
            });
            console.warn('Decision created successfully:', response);
            // Invalidate investment queries to refresh status
            queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
            queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
            queryClient.invalidateQueries({ queryKey: workflowKeys.statusHistory(investmentId) });
            queryClient.invalidateQueries({ queryKey: workflowKeys.decisions(investmentId) });
          } catch (decisionError) {
            console.error('Failed to mark investment as planned:', decisionError);
          }
        }
      }
      onClose();
    } catch (error) {
      console.error('Failed to save roadmap item:', error);
    }
  };

  const handleDelete = async () => {
    if (!item) {
      return;
    }

    const investmentIdToRevert = item.investment_id;

    try {
      await deleteMutation.mutateAsync(item.roadmap_item_id);

      // After deleting, revert the investment status based on decision history
      if (canManageStatus) {
        try {
          // Fetch the decision history for this investment
          const decisionsResponse = await workflowService.getDecisions(investmentIdToRevert);
          // Extract from success wrapper: response.data = { success, data: { decisions, ... } }
          const responseData = (decisionsResponse.data as unknown as Record<string, unknown>)?.data || decisionsResponse.data;
          const wrappedData = responseData as Record<string, unknown>;
          const decisions = (wrappedData?.decisions || []) as Record<string, unknown>[];

          // Check if the investment was ever approved (investment_status = 'A')
          const wasEverApproved = decisions.some((d: Record<string, unknown>) => d.investment_status === 'A');

          // Set status back to approved if it was ever approved, otherwise submitted
          const newStatus = wasEverApproved ? 'A' : 'S';
          const rationale = wasEverApproved
            ? 'Removed from roadmap - reverted to approved'
            : 'Removed from roadmap - reverted to submitted';

          await workflowService.createDecision(investmentIdToRevert, {
            investment_status: newStatus,
            decision_rationale: rationale,
          });

          // Invalidate investment queries to refresh status
          queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentIdToRevert) });
          queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
          queryClient.invalidateQueries({
            queryKey: workflowKeys.statusHistory(investmentIdToRevert),
          });
          queryClient.invalidateQueries({ queryKey: workflowKeys.decisions(investmentIdToRevert) });
        } catch (revertError) {
          console.error('Failed to revert investment status:', revertError);
        }
      }

      onClose();
    } catch (error) {
      console.error('Failed to delete roadmap item:', error);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isEditing ? 'Edit Roadmap Item' : 'Add Roadmap Item'}
            </h2>
            <button
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Theme Selection */}
              <div>
                <label
                  htmlFor="theme"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Theme *
                </label>
                <select
                  id="theme"
                  value={themeId}
                  onChange={(e) => setThemeId(parseInt(e.target.value))}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                  required
                >
                  {themes.map((theme) => (
                    <option key={theme.roadmap_theme_id} value={theme.roadmap_theme_id}>
                      {theme.theme_title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Investment Selection */}
              <div>
                <label
                  htmlFor="investment"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Investment *
                </label>
                {isEditing && item?.investment_id ? (
                  // Show linked investment title when editing
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`/investments/${item.investment_id}`);
                    }}
                    className="w-full text-left rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 transition-colors flex items-center justify-between group"
                  >
                    <span className="font-medium">
                      {item.investment_title || `Investment #${item.investment_id}`}
                    </span>
                    <svg
                      className="h-4 w-4 opacity-60 group-hover:opacity-100"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                      />
                    </svg>
                  </button>
                ) : (
                  // Show search and select for new items
                  <>
                    <input
                      type="text"
                      placeholder="Search investments..."
                      value={investmentSearch}
                      onChange={(e) => setInvestmentSearch(e.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 mb-2"
                    />
                    <select
                      id="investment"
                      value={investmentId}
                      onChange={(e) => setInvestmentId(parseInt(e.target.value))}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                      required
                      size={Math.min(5, Math.max(3, filteredInvestments.length))}
                    >
                      <option value="">Select an investment</option>
                      {filteredInvestments.map((inv) => (
                        <option key={inv.investment_id} value={inv.investment_id}>
                          {inv.title}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="startDate"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      updateEffortFromDates(e.target.value, endDate);
                    }}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="endDate"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    End Date *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      updateEffortFromDates(startDate, e.target.value);
                    }}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                    required
                  />
                </div>
              </div>

              {/* Commitment Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="isCommitted"
                    checked={isCommitted}
                    onChange={(e) => setIsCommitted(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  <label
                    htmlFor="isCommitted"
                    className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Is Committed
                  </label>
                </div>

                {isCommitted && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="committedDate"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Committed Date
                      </label>
                      <input
                        type="date"
                        id="committedDate"
                        value={committedDate}
                        onChange={(e) => setCommittedDate(e.target.value)}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="deliveryDate"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Delivery Date
                      </label>
                      <input
                        type="date"
                        id="deliveryDate"
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Tolerance Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="toleranceDays"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Tolerance (Days)
                  </label>
                  <input
                    type="number"
                    id="toleranceDays"
                    value={toleranceDays}
                    onChange={(e) => setToleranceDays(parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label
                    htmlFor="tolerancePercent"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Tolerance (%)
                  </label>
                  <input
                    type="number"
                    id="tolerancePercent"
                    value={tolerancePercent}
                    onChange={(e) => setTolerancePercent(parseInt(e.target.value) || 0)}
                    min="0"
                    max="100"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                </div>
              </div>

              {/* Effort Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="estimatedEffortDays"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Estimated Effort (Days)
                    {isAutoEffort && (
                      <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">(auto)</span>
                    )}
                  </label>
                  <input
                    type="number"
                    id="estimatedEffortDays"
                    value={estimatedEffortDays}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setEstimatedEffortDays(val);
                      setEffortManualOverride(val !== autoEffort);
                    }}
                    min="0"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  {estimatedEffortDays !== autoEffort && autoEffort > 0 && (
                    <button
                      type="button"
                      onClick={() => setEstimatedEffortDays(autoEffort)}
                      className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {autoEffort} business days (apply)
                    </button>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="estimatedRemainingDays"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Remaining (Days)
                  </label>
                  <input
                    type="number"
                    id="estimatedRemainingDays"
                    value={estimatedRemainingDays}
                    onChange={(e) => setEstimatedRemainingDays(parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                </div>
              </div>

              {/* Priority Flag */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="topPriorityFlag"
                  checked={topPriorityFlag}
                  onChange={(e) => setTopPriorityFlag(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500 dark:focus:ring-amber-400"
                />
                <label
                  htmlFor="topPriorityFlag"
                  className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Top Priority
                </label>
              </div>

              {/* Baseline Section - edit mode only */}
              {isEditing && item && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Baseline
                    </span>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const updated = await baselineMutation.mutateAsync(item.roadmap_item_id);
                          // Update local state so the UI reflects the new baseline immediately
                          setBaselineDate(updated.baseline_date ? String(updated.baseline_date) : null);
                          setBaselineStartDate(updated.baseline_start_date ? String(updated.baseline_start_date) : null);
                          setBaselineEndDate(updated.baseline_end_date ? String(updated.baseline_end_date) : null);
                        } catch (err) {
                          console.error('Failed to set baseline:', err);
                        }
                      }}
                      disabled={baselineMutation.isPending}
                      className="inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
                    >
                      {baselineMutation.isPending
                        ? 'Setting...'
                        : baselineDate
                          ? 'Reset Baseline'
                          : 'Set Baseline'}
                    </button>
                  </div>
                  {baselineDate ? (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Set on{' '}
                        {new Date(baselineDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Start:{' '}
                        {baselineStartDate
                          ? new Date(baselineStartDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '-'}
                        {' / '}
                        End:{' '}
                        {baselineEndDate
                          ? new Date(baselineEndDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '-'}
                      </p>
                      <VarianceSummary
                        baselineStart={
                          baselineStartDate
                            ? new Date(baselineStartDate).toISOString().split('T')[0]
                            : ''
                        }
                        baselineEnd={
                          baselineEndDate
                            ? new Date(baselineEndDate).toISOString().split('T')[0]
                            : ''
                        }
                        currentStart={startDate}
                        currentEnd={endDate}
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400">No baseline set</p>
                  )}
                </div>
              )}

              {/* Mark as Planned - only shown when creating and user has permission */}
              {!isEditing && canManageStatus && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="markAsPlanned"
                      checked={markAsPlanned}
                      onChange={(e) => setMarkAsPlanned(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:focus:ring-purple-400"
                    />
                    <label
                      htmlFor="markAsPlanned"
                      className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Mark this investment as planned
                    </label>
                  </div>
                  <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
                    Changes the investment status to &quot;Planned&quot; and logs the decision
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-6 py-4">
              {isEditing ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  className="inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              ) : (
                <div />
              )}

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !themeId || !investmentId || !startDate || !endDate}
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Item'}
                </button>
              </div>
            </div>
          </form>

          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mx-4 max-w-sm">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Delete Item?
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Are you sure you want to delete this roadmap item? This action cannot be undone.
                </p>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="rounded-md px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoadmapItemEditor;
